import React, { useState } from 'react';
import {
  Layers,
  Filter,
  ShieldCheck,
  ShieldAlert,
  Plus,
  RefreshCw,
  Users,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Lock,
  Search
} from 'lucide-react';
import { INITIAL_AUDIENCE_SEGMENTS } from '../../data/growthData';
import { AudienceSegment } from '../../types';

export const SegmentBuilderView: React.FC = () => {
  const [segments, setSegments] = useState<AudienceSegment[]>(INITIAL_AUDIENCE_SEGMENTS);
  const [selectedSegment, setSelectedSegment] = useState<AudienceSegment | null>(INITIAL_AUDIENCE_SEGMENTS[0]);
  
  // Rule Builder Test Inputs
  const [testRuleAttribute, setTestRuleAttribute] = useState<string>('product_used');
  const [testRuleOperator, setTestRuleOperator] = useState<string>('EQUALS');
  const [testRuleValue, setTestRuleValue] = useState<string>('salary_calculator');
  const [privacyAlert, setPrivacyAlert] = useState<string | null>(null);

  const handleRuleChange = (attribute: string, val: string) => {
    setTestRuleAttribute(attribute);
    setTestRuleValue(val);

    // Privacy Firewall Interceptor
    const prohibitedKeywords = ['salary', 'income', 'debt', 'loan_amount', 'tax_paid', 'bank', 'ssn', '$'];
    const isProhibited = prohibitedKeywords.some(kw => val.toLowerCase().includes(kw) && !val.includes('_tool') && !val.includes('calculator'));

    if (isProhibited || attribute === 'raw_salary_amount' || attribute === 'debt_balance') {
      setPrivacyAlert('PRIVACY VIOLATION DETECTED: Segment rules cannot filter on exact salaries, debts, or financial dollar amounts. Use non-sensitive behavioral tags (e.g. "used_salary_tool = true").');
    } else {
      setPrivacyAlert(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-indigo-100 text-indigo-900">
              P12.2 — Audience & Segment Engine
            </span>
            <span className="flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
              Privacy Firewall Guard Enabled
            </span>
          </div>
          <h2 className="text-xl font-black text-slate-900 tracking-tight">
            Audience Segmentation & Dynamic Audience Builder
          </h2>
          <p className="text-xs text-slate-600 mt-1 max-w-3xl">
            Defines privacy-compliant audience segments based on non-sensitive product usage, market preferences, and lifecycle stages. Automatically enforces privacy checks on all segment rules.
          </p>
        </div>

        <button className="px-3.5 py-2 rounded-lg bg-blue-900 text-white text-xs font-bold hover:bg-blue-800 transition-colors flex items-center gap-1.5 cursor-pointer shadow-xs">
          <Plus className="w-4 h-4" />
          <span>Create New Segment</span>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Segments List */}
        <div className="lg:col-span-1 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
              Active Segments ({segments.length})
            </h3>
            <span className="text-[11px] text-slate-500 font-medium">Updated Realtime</span>
          </div>

          <div className="space-y-2">
            {segments.map((seg) => {
              const isSelected = selectedSegment?.id === seg.id;
              return (
                <div
                  key={seg.id}
                  onClick={() => setSelectedSegment(seg)}
                  className={`p-4 rounded-xl border transition-all cursor-pointer ${
                    isSelected
                      ? 'bg-blue-50/50 border-blue-600 ring-1 ring-blue-600 shadow-xs'
                      : 'bg-white border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1.5">
                    <span className="font-bold text-xs text-slate-900">{seg.name}</span>
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800 shrink-0">
                      PASSED
                    </span>
                  </div>
                  <p className="text-xs text-slate-600 line-clamp-2 mb-2">
                    {seg.description}
                  </p>
                  <div className="flex items-center justify-between text-[11px] text-slate-500 pt-2 border-t border-slate-100">
                    <span className="font-semibold text-slate-700">
                      ~{seg.estimatedAudienceSize.toLocaleString()} users
                    </span>
                    <span className="text-slate-400 font-mono text-[10px]">
                      {seg.refreshFrequency}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Selected Segment Details & Rule Builder Test */}
        <div className="lg:col-span-2 space-y-6">
          {selectedSegment && (
            <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs space-y-5">
              <div className="flex items-start justify-between pb-4 border-b border-slate-100">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-bold text-slate-900">{selectedSegment.name}</h3>
                    <span className="px-2 py-0.5 rounded text-xs font-mono bg-slate-100 text-slate-700 border border-slate-200">
                      {selectedSegment.segmentKey}
                    </span>
                  </div>
                  <p className="text-xs text-slate-600 mt-1">{selectedSegment.description}</p>
                </div>

                <div className="text-right">
                  <span className="text-xs text-slate-500 block">Est. Audience</span>
                  <span className="text-lg font-black text-slate-900">
                    {selectedSegment.estimatedAudienceSize.toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Segment Metadata Badges */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <span className="text-slate-500 block text-[11px]">Classification</span>
                  <span className="font-bold text-slate-800">{selectedSegment.privacyClassification}</span>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <span className="text-slate-500 block text-[11px]">Active Rules</span>
                  <span className="font-bold text-slate-800">{selectedSegment.rulesCount} Rules</span>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <span className="text-slate-500 block text-[11px]">Refresh Frequency</span>
                  <span className="font-bold text-slate-800">{selectedSegment.refreshFrequency}</span>
                </div>
                <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <span className="text-emerald-700 block text-[11px]">Privacy Review</span>
                  <span className="font-bold text-emerald-900">PASSED 100%</span>
                </div>
              </div>

              {/* Interactive Rule Builder & Privacy Interceptor Simulator */}
              <div className="pt-2 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
                    <Filter className="w-3.5 h-3.5 text-blue-900" />
                    Interactive Rule Builder & Privacy Firewall Tester
                  </h4>
                  <span className="text-[11px] text-slate-500">Simulate adding rules</span>
                </div>

                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="text-[11px] font-bold text-slate-700 block mb-1">
                        Attribute Name:
                      </label>
                      <select
                        value={testRuleAttribute}
                        onChange={(e) => handleRuleChange(e.target.value, testRuleValue)}
                        className="w-full text-xs border border-slate-300 rounded px-2.5 py-1.5 bg-white font-medium text-slate-800"
                      >
                        <option value="product_used">used_product_tool (Safe Tag)</option>
                        <option value="has_fx_alert">has_active_fx_alert (Safe Tag)</option>
                        <option value="market_preference">market_preference (Safe Tag)</option>
                        <option value="raw_salary_amount">raw_salary_amount (PROHIBITED)</option>
                        <option value="debt_balance">debt_balance (PROHIBITED)</option>
                      </select>
                    </div>

                    <div>
                      <label className="text-[11px] font-bold text-slate-700 block mb-1">
                        Operator:
                      </label>
                      <select
                        value={testRuleOperator}
                        onChange={(e) => setTestRuleOperator(e.target.value)}
                        className="w-full text-xs border border-slate-300 rounded px-2.5 py-1.5 bg-white font-medium text-slate-800"
                      >
                        <option value="EQUALS">EQUALS</option>
                        <option value="GREATER_THAN">GREATER THAN</option>
                        <option value="CONTAINS">CONTAINS</option>
                      </select>
                    </div>

                    <div>
                      <label className="text-[11px] font-bold text-slate-700 block mb-1">
                        Rule Value:
                      </label>
                      <input
                        type="text"
                        value={testRuleValue}
                        onChange={(e) => handleRuleChange(testRuleAttribute, e.target.value)}
                        placeholder="e.g. salary_calculator or $85,000"
                        className="w-full text-xs border border-slate-300 rounded px-2.5 py-1.5 bg-white font-medium text-slate-800"
                      />
                    </div>
                  </div>

                  {privacyAlert ? (
                    <div className="p-3 bg-rose-50 border border-rose-300 rounded-lg flex items-start gap-2 text-rose-900 text-xs font-medium">
                      <AlertTriangle className="w-4 h-4 text-rose-700 shrink-0 mt-0.5" />
                      <span>{privacyAlert}</span>
                    </div>
                  ) : (
                    <div className="p-3 bg-emerald-50 border border-emerald-300 rounded-lg flex items-center gap-2 text-emerald-900 text-xs font-medium">
                      <CheckCircle2 className="w-4 h-4 text-emerald-700 shrink-0" />
                      <span>RULE PRIVACY VALIDATED: This rule complies 100% with YamooPay Non-Sensitive CRM Guidelines.</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
